import { useEffect, useState } from "react";
import {
  faChartBar,
  faQuestionCircle,
  faSignOutAlt,
  faCog,
  faUsers,
  faClipboardList,
  faMobile,
  faUndoAlt,
  faGift,
} from "@fortawesome/free-solid-svg-icons";
import Router, { useRouter } from "next/router";
import { signOut } from "next-auth/react";
import { useMenu } from "@/hooks/useMenu";
import type { ISidebarLink } from "@/types/layout/navigation";
import { onShowConfirmAlert } from "@dinesapp/dines-ui/utils/alerts";
import MainSideBarLinks from "./links";
import SideBarProfile from "./profile";
import SideBarTitle from "./title";
import { useVenue } from "@/hooks/useVenue";
import type { Venue } from "@/types/api/contracts/venue";
import VenueModal from "./VenueModal";
import Button from "@dinesapp/dines-ui/components/Buttons/Button";
import Icon from "@dinesapp/dines-ui/components/Icon";
import { faUserChef } from "@fortawesome/pro-solid-svg-icons";
import { FeatureFlag } from "@/types/auth/FeatureFlags";

interface IMainSideBar {
  organisation?: Venue;
  selected: string;
  setCollapseSidebar: (collapseSidebar: boolean) => void;
  collapseSidebar: boolean;
}

const MainSideBar: React.FC<IMainSideBar> = ({
  organisation,
  selected,
  setCollapseSidebar,
  collapseSidebar,
}) => {
  const router = useRouter();
  const { menuOpen, toggleMenu, setMenuOpen } = useMenu();
  const {
    selectedVenue,
    setSelectedVenue,
    venues,
    isVenueSpecific,
    getFeatureFlags,
  } = useVenue();
  const [isVenueModalOpen, setIsVenueModalOpen] = useState(false);
  const onToggleVenueModal = () => {
    setIsVenueModalOpen(!isVenueModalOpen);
  };

  const featureFlags = getFeatureFlags();

  const onSignOut = () => {
    onShowConfirmAlert({
      labels: {
        id: "logout-confirm-alert",
        title: "Are you sure you want to log out?",
        yesLabel: "Log out",
      },

      visibilityOptions: {
        showNoButton: false,
        showYesButton: true,
      },

      onYes: () => {
        signOut();
      },
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <No dependencies needed>
  useEffect(() => {
    const handleRouteChange = () => {
      setMenuOpen(false);
    };

    Router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      Router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, []);

  useEffect(() => {
    if (router.pathname.startsWith("/redeemables")) {
      return setCollapseSidebar(true);
    }
    return setCollapseSidebar(false);
  }, [router.pathname, setCollapseSidebar]);

  const links: ISidebarLink[] = [
    {
      name: "Linked Accounts",
      type: "link",
      url: "/accounts",
      id: "accounts",
      external: false,
      icon: faUsers,
      isVisible: true,
    },
    {
      name: "Global Menus",
      type: "link",
      url: "/operations",
      id: "operations",
      external: false,
      icon: faClipboardList,
      isVisible: true,
    },
    {
      name: "Past Orders",
      type: "link",
      url: "/past-orders",
      id: "past-orders",
      external: false,
      icon: faUndoAlt,
      isVisible: true,
    },
    {
      name: "Reports",
      type: "link",
      url: "/reports",
      id: "reports",
      external: false,
      icon: faChartBar,
      isVisible: true,
    },
    {
      name: "Devices",
      type: "link",
      url: "/devices",
      id: "devices",
      external: false,
      icon: faMobile,
      isVisible: true,
    },
    {
      name: "Redeemables",
      type: "link",
      url: "/redeemables/code-lookup",
      id: "redeemables",
      external: false,
      icon: faGift,
      isVisible: featureFlags[FeatureFlag.RedeemablesV2],
    },
    {
      name: "Settings",
      type: "link",
      url: "/settings",
      id: "settings",
      external: false,
      icon: faCog,
      isVisible: true,
    },

    {
      name: "Support",
      type: "link",
      url: "https://dines.co.uk/chat",
      id: "support",
      external: true,
      icon: faQuestionCircle,
      isVisible: true,
    },
    {
      name: "Sign out",
      type: "action",
      id: "logout",
      icon: faSignOutAlt,
      action: onSignOut,
      external: false,
      isVisible: true,
    },
  ].filter((link) => link.isVisible);

  return (
    <>
      {isVenueModalOpen && (
        <VenueModal
          onToggleModal={onToggleVenueModal}
          venues={venues}
          selectedVenue={venues.find((venue) => venue.id === selectedVenue)}
          setSelectedVenue={setSelectedVenue}
        />
      )}
      <div
        id="main-sidebar"
        className="no-scrollbar flex h-full flex-col overflow-x-hidden text-white "
      >
        <SideBarTitle
          collapseSidebar={collapseSidebar}
          setCollapseSidebar={setCollapseSidebar}
          menuOpen={menuOpen}
          toggleMenu={toggleMenu}
        />
        {isVenueSpecific && (
          <div className="m-4 ">
            <h1 className="text-left font-bold text-lg">Account</h1>
            <div className="mt-2 rounded-xl ring-2 ring-dines-green">
              {!collapseSidebar ? (
                <Button
                  text={
                    venues.find((venue) => venue.id === selectedVenue)?.name
                  }
                  buttonType=" text-white bg-dines-purple"
                  onClick={onToggleVenueModal}
                  width="w-full"
                  extraClasses="whitespace-nowrap truncate"
                  margin=""
                />
              ) : (
                <>
                  <Button
                    text={
                      venues.find((venue) => venue.id === selectedVenue)?.name
                    }
                    buttonType=" text-white bg-dines-purple"
                    onClick={onToggleVenueModal}
                    width="w-full"
                    extraClasses="whitespace-nowrap truncate md:hidden"
                    margin=""
                  />
                  <div
                    className=" hidden cursor-pointer items-center justify-center py-3 md:flex"
                    onClick={onToggleVenueModal}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        onToggleVenueModal();
                      }
                    }}
                  >
                    <Icon icon={faUserChef} size="lg" className="text-white" />
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        <MainSideBarLinks
          links={links}
          selected={selected}
          collapseSidebar={collapseSidebar}
        />
        <SideBarProfile
          organisation={organisation}
          collapseSidebar={collapseSidebar}
        />
      </div>
    </>
  );
};

export default MainSideBar;
